import {useState} from 'react';
import {Modal} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  LeftOutlined,
  PlayCircleFilled,
  RightOutlined,
} from '@ant-design/icons';
import styles from './ConversationThread.module.scss';
import {
  formatAgentFirstName,
  parseEventPayload,
  stripAiThinking,
} from '../helpdeskTicketsUtils';

const formatTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

/**
 * Render chat text with basic rich formatting — preserves newlines and
 * supports inline **bold**, *italic*, `code`, and [links](url). Escapes HTML
 * first to keep raw input safe.
 */
const escapeHtml = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderRichText = text => {
  if (!text) return {__html: ''};
  let html = escapeHtml(text);
  html = html
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer noopener">$1</a>',
    )
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    // Auto-link bare URLs (only ones not already inside an anchor's text)
    .replace(
      /(^|[^"'>])(https?:\/\/[^\s<]+)/g,
      '$1<a href="$2" target="_blank" rel="noreferrer noopener">$2</a>',
    );
  return {__html: html};
};

const RichText = ({text, className}) => (
  <div className={className} dangerouslySetInnerHTML={renderRichText(text)} />
);

const isUrl = url => /^https?:\/\//i.test(url || '');

const isImageFile = file =>
  file?.type?.startsWith('image/') ||
  /\.(png|jpe?g|gif|webp|svg)$/i.test(file?.name || '');

const isVideoFile = file =>
  file?.type?.startsWith('video/') ||
  /\.(mp4|mov|webm|m4v)$/i.test(file?.name || '');

const extOf = file => {
  const fromName = (file?.name || '').split('.').pop();
  if (fromName && fromName.length <= 5) return fromName.toUpperCase();
  const fromType = (file?.type || '').split('/').pop();
  return (fromType || 'FILE').toUpperCase();
};

const MediaPreviewModal = ({items, index, onClose, onPrev, onNext}) => {
  if (index == null || !items?.length) return null;
  const item = items[index];
  const hasNav = items.length > 1;
  const isVideo = isVideoFile(item);
  return (
    <Modal
      open={index != null}
      onCancel={onClose}
      footer={null}
      width="auto"
      centered
      destroyOnClose
      className={styles.imagePreviewModal}
      bodyStyle={{padding: 0, background: 'transparent'}}>
      <div className={styles.imagePreviewBody}>
        {hasNav ? (
          <button
            type="button"
            className={`${styles.imagePreviewNav} ${styles.imagePreviewPrev}`}
            onClick={onPrev}>
            <LeftOutlined />
          </button>
        ) : null}
        {isVideo ? (
          <video
            key={item.url}
            src={item.url}
            controls
            autoPlay
            className={styles.imagePreviewImg}
          />
        ) : (
          <img
            src={item.url}
            alt={item.name || 'image'}
            className={styles.imagePreviewImg}
          />
        )}
        {hasNav ? (
          <button
            type="button"
            className={`${styles.imagePreviewNav} ${styles.imagePreviewNext}`}
            onClick={onNext}>
            <RightOutlined />
          </button>
        ) : null}
      </div>
      <div className={styles.imagePreviewCaption}>
        <span>
          {item.name}
          {hasNav ? `  ·  ${index + 1} / ${items.length}` : ''}
        </span>
        <a href={item.url} target="_blank" rel="noreferrer noopener" download>
          <DownloadOutlined /> Open
        </a>
      </div>
    </Modal>
  );
};

const MessageFiles = ({files}) => {
  const [previewIndex, setPreviewIndex] = useState(null);

  if (!Array.isArray(files) || !files.length) return null;

  const media = files.filter(
    f => isUrl(f?.url) && (isImageFile(f) || isVideoFile(f)),
  );
  const docs = files.filter(f => !isImageFile(f) && !isVideoFile(f));
  // Files whose URL is local-only (e.g. simulator path) — show as broken pills.
  const unreachable = files.filter(
    f => !isUrl(f?.url) && (isImageFile(f) || isVideoFile(f)),
  );

  const gridClass = media.length >= 2 ? styles.fileGridTwo : styles.fileGridOne;

  return (
    <div className={styles.fileStack}>
      {media.length ? (
        <div className={gridClass}>
          {media.map((f, i) => {
            const video = isVideoFile(f);
            return (
              <button
                key={`${f.url}-${i}`}
                type="button"
                className={styles.fileThumbBtn}
                onClick={() => setPreviewIndex(i)}>
                {video ? (
                  <video
                    src={f.url}
                    className={styles.fileThumb}
                    muted
                    preload="metadata"
                  />
                ) : (
                  <img
                    src={f.url}
                    alt={f.name || 'image'}
                    className={styles.fileThumb}
                  />
                )}
                {video ? (
                  <span
                    className={`${styles.fileThumbMask} ${styles.fileThumbVideoMask}`}>
                    <PlayCircleFilled style={{fontSize: 40}} />
                  </span>
                ) : (
                  <span className={styles.fileThumbMask}>View</span>
                )}
              </button>
            );
          })}
        </div>
      ) : null}

      <MediaPreviewModal
        items={media}
        index={previewIndex}
        onClose={() => setPreviewIndex(null)}
        onPrev={() =>
          setPreviewIndex(i => (i - 1 + media.length) % media.length)
        }
        onNext={() => setPreviewIndex(i => (i + 1) % media.length)}
      />

      {[...docs, ...unreachable].map((f, i) => {
        const reachable = isUrl(f?.url);
        return (
          <a
            key={`${f.url || f.name}-${i}`}
            href={reachable ? f.url : undefined}
            target={reachable ? '_blank' : undefined}
            rel={reachable ? 'noreferrer noopener' : undefined}
            className={`${styles.filePill} ${
              !reachable ? styles.filePillBroken : ''
            }`}
            onClick={e => {
              if (!reachable) e.preventDefault();
            }}
            title={reachable ? f.name : `${f.name} (unavailable)`}>
            <FileTextOutlined className={styles.filePillIcon} />
            <span className={styles.filePillName}>{f.name || 'file'}</span>
            <span className={styles.filePillExt}>{extOf(f)}</span>
            {reachable ? (
              <DownloadOutlined className={styles.filePillDownload} />
            ) : (
              <span className={styles.filePillUnavailable}>unavailable</span>
            )}
          </a>
        );
      })}
    </div>
  );
};

const renderSystemText = (payload, agent) => {
  if (payload.textKey === 'TICKET_ASSIGNED') {
    const liveName = formatAgentFirstName(agent, null);
    if (liveName) return `Ticket assigned to ${liveName}.`;
  }
  return payload.text || '';
};

const MessageBubble = ({msg}) => {
  const payload = parseEventPayload(msg.payload);
  const eventType = msg.eventType;
  const liveAgent = msg.agent || null;

  switch (eventType) {
    case 'FLOW_STEP':
      return (
        <div className={styles.guidedFlowStep}>
          <span className={styles.dot}></span>
          <span className={styles.text}>
            User selected: {payload.label || 'Unknown option'}
          </span>
        </div>
      );
    case 'USER_MESSAGE':
      return (
        <div className={styles.userMessageWrapper}>
          {payload.text ? (
            <RichText text={payload.text} className={styles.userBubble} />
          ) : null}
          <MessageFiles files={payload.files} />
          <div className={styles.timestamp}>{formatTime(msg.createdAt)}</div>
        </div>
      );
    case 'AI_MESSAGE':
      return (
        <div className={styles.aiMessageWrapper}>
          <div className={styles.avatar}>S</div>
          <div className={styles.aiContentContainer}>
            <div className={styles.aiHeader}>
              <span className={styles.name}>Sassandra</span>
            </div>
            <RichText
              text={stripAiThinking(payload.text)}
              className={styles.aiBubble}
            />
          </div>
        </div>
      );
    case 'INTERNAL_NOTE': {
      const isCsat = payload.source === 'csat';
      const csatScoreInPayload =
        typeof payload.csatScore === 'number' ? payload.csatScore : null;
      return (
        <div
          className={`${styles.internalNote} ${isCsat ? styles.csatNote : ''}`}>
          <div className={styles.noteHeader}>
            {isCsat ? (
              <>
                <span className={styles.csatIcon}>&#9733;</span> Customer
                feedback
                {csatScoreInPayload !== null ? (
                  <span className={styles.csatBadge}>
                    {csatScoreInPayload}/5
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <span className={styles.lockIcon}>&#128274;</span> Internal Note
              </>
            )}
          </div>
          <RichText text={payload.text} className={styles.noteContent} />
        </div>
      );
    }
    case 'SYSTEM_EVENT': {
      if (Array.isArray(payload.faqs) && payload.faqs.length) {
        return (
          <div className={styles.faqShownWrapper}>
            <div className={styles.faqIntro}>
              {payload.text || 'Here are some articles that might help:'}
            </div>
            <div className={styles.faqList}>
              {payload.faqs.map(faq => (
                <details key={faq.postid} className={styles.faqCard}>
                  <summary className={styles.faqQuestion}>
                    <span className={styles.faqIcon}>&#128196;</span>
                    <span>{faq.question}</span>
                    <span className={styles.faqPostId}>#{faq.postid}</span>
                  </summary>
                  <div className={styles.faqAnswer}>{faq.answer}</div>
                </details>
              ))}
            </div>
          </div>
        );
      }
      const isBot = payload.sender === 'bot';
      if (isBot) {
        return (
          <div className={styles.aiMessageWrapper}>
            <div className={styles.avatar}>S</div>
            <div className={styles.aiContentContainer}>
              <div className={styles.aiHeader}>
                <span className={styles.name}>Sassandra</span>
              </div>
              <RichText text={payload.text} className={styles.aiBubble} />
            </div>
          </div>
        );
      }
      return (
        <div className={styles.systemEvent}>
          <div className={styles.eventLine}></div>
          <span className={styles.eventText}>
            {renderSystemText(payload, liveAgent)}
          </span>
          <div className={styles.eventLine}></div>
        </div>
      );
    }
    case 'AGENT_MESSAGE': {
      const displayName =
        formatAgentFirstName(liveAgent, payload.agent_name) || 'Agent';
      return (
        <div className={styles.agentMessageWrapper}>
          {payload.text ? (
            <RichText text={payload.text} className={styles.agentBubble} />
          ) : null}
          <MessageFiles files={payload.files} />
          <div className={styles.timestamp}>
            <span className={styles.agentName}>{displayName}</span>
            {formatTime(msg.createdAt)}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
};

export const ConversationThread = ({events = []}) => {
  // The detail thread must read chronologically (oldest → newest) regardless
  // of the order the API returns events in. The DESC sort only applies to the
  // ticket-list preview (getAdminTickets), not this view.
  const ordered = [...events].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return (
    <div className={styles.threadContainer}>
      {ordered.map(msg => (
        <MessageBubble key={msg.id} msg={msg} />
      ))}
    </div>
  );
};
